import {
  useLocationMutation,
  useLocationQuery,
  useUpdateLocationMutation,
} from "../../api/locationApi.ts";
import { useEffect } from "react";
import { useNavigate, useParams } from "react-router";
import Container from "../../components/Container.tsx";
import ContainerHeader from "../../components/ContainerHeader.tsx";
import LoadingState from "../../components/LoadingState.tsx";
import ErrorState from "../../components/ErrorState.tsx";
import BackButton from "../../components/navigation/BackButton.tsx";
import LocationForm, {
  LocationFormData,
} from "../../components/location/LocationForm.tsx";
import { useLocationFormState } from "../../components/location/LocationFormState.ts";
import { toast } from "react-hot-toast";
import { t } from "@lingui/core/macro";

export default function EditSingleLocation() {
  const { userId, locationId } = useParams();
  const navigate = useNavigate();
  const isCreating = locationId === "new";

  const {
    data: location,
    isLoading,
    error,
    refetch,
  } = useLocationQuery(locationId!, userId, {
    enabled: !isCreating, // Only fetch if we're editing an existing location
  });

  const { mutate: updateLocation, isPending: updatePending } =
    useUpdateLocationMutation();

  const { mutate: createLocation, isPending: createPending } =
    useLocationMutation();

  const { formState, initializeFromLocation, updateFormState } =
    useLocationFormState();

  useEffect(() => {
    if (isCreating) {
      // Initialize with default values for creating a new location
      updateFormState({
        name: "",
        instruments: [],
        locationType: "IN_PERSON",
        selectedAddress: null,
      });
    } else if (location) {
      initializeFromLocation(location);
    }
  }, [location, initializeFromLocation, updateFormState, isCreating]);

  const handleSubmit = (formData: LocationFormData) => {
    if (isCreating) {
      // Create new location
      const locationRequest = {
        name: formData.name,
        instruments: formData.instruments,
        locationType: formData.locationType,
        longitude: formData.selectedAddress?.longitude,
        latitude: formData.selectedAddress?.latitude,
        countryCode: formData.selectedAddress?.countryCode,
        postalCode: formData.selectedAddress?.postalCode,
        city: formData.selectedAddress?.city,
        streetName: formData.selectedAddress?.streetName,
        houseNumber: formData.selectedAddress?.houseNumber,
        region: formData.selectedAddress?.region,
        buildingName: formData.selectedAddress?.buildingName,
        subBuildingName: formData.selectedAddress?.subBuildingName,
        timeZone: formData.selectedAddress!.timeZone,
      };

      createLocation(locationRequest, {
        onSuccess: () => {
          toast.success(t`Location successfully created!`);
          navigate(`/mystudio/locations`);
        },
        onError: (error) => console.error("Failed to create location:", error),
      });
    } else {
      // Update existing location
      const updatedFormData = {
        name: formData.name,
        locationType: formData.locationType,
        instruments: formData.instruments,
        ...formData.selectedAddress,
      };
      const updatedLocation = {
        ...location,
        ...updatedFormData,
      };

      updateLocation(
        { locationId: locationId!, locationData: updatedLocation },
        {
          onSuccess: () => {
            refetch().catch(console.error);
            navigate(`/mystudio/locations`);
          },
          onError: (error) =>
            console.error("Failed to update location:", error),
        },
      );
    }
  };

  const handleCancel = () => {
    navigate(`/mystudio/locations`);
  };

  if (isLoading && !isCreating) {
    return <LoadingState message={t`Loading...`} />;
  }

  if (!isCreating && (error || !location)) {
    return (
      <ErrorState
        message={t`Could not load location details. Please try again.`}
        onGoBack={handleCancel}
      />
    );
  }

  return (
    <Container className="mx-auto flex w-full max-w-3xl flex-col">
      <BackButton to={`/mystudio/locations`} label={t`Back to All Locations`} />

      <ContainerHeader>
        {isCreating ? t`Create Location` : t`Edit Location`}
      </ContainerHeader>

      {formState && (
        <LocationForm
          initialData={formState}
          onSubmit={handleSubmit}
          isSubmitting={isCreating ? createPending : updatePending}
          submitLabel={isCreating ? t`Create` : t`Save`}
        />
      )}
    </Container>
  );
}
